import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Services } from "./components/Services";
import { LandingExamples } from "./components/LandingExamples";
import { Templates } from "./components/Templates";
import { HowItWorks } from "./components/HowItWorks";
import { Benefits } from "./components/Benefits";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { VerifyEmail } from "./pages/VerifyEmail";
import { Pricing } from "./pages/Pricing";
import { AdminDashboard } from "./pages/AdminDashboard";
import { PropertyForm } from "./pages/PropertyForm";
import { PropertyLanding } from "./pages/PropertyLanding";
import { SuperAdmin } from "./pages/SuperAdmin";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import { AuthCallback } from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import { ProtectedRoute } from "./components/ProtectedRoute";

const Home = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <Services />
      <LandingExamples />
      <Templates />
      <HowItWorks />
      <Benefits />
      <Contact />
      <Footer />
    </div>
  );
};

// Router wrapper to handle Google OAuth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (legacy Emergent flow)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/precios" element={<Pricing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      
      {/* Property Landing Pages (Public) */}
      <Route path="/propiedades/:slug" element={<PropertyLanding />} />
      
      {/* Legacy admin login redirect */}
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      
      {/* Protected Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/properties/new"
        element={
          <ProtectedRoute>
            <PropertyForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/properties/edit/:id"
        element={
          <ProtectedRoute>
            <PropertyForm />
          </ProtectedRoute>
        }
      />
      
      {/* Super Admin */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute>
            <SuperAdmin />
          </ProtectedRoute>
        }
      />

      {/* Payment Success */}
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Platform Pixel (2770682049943810) - ONLY on InmoPages marketing/sales pages
// Property landing pages use their own pixel. Admin/dashboard NEVER loads this.
const PLATFORM_PIXEL_ID = '2770682049943810';
let _lastPixelPage = null; // Prevent double PageView on same page

function loadFbPixelSDK() {
  if (window.fbq) return;
  const n = window.fbq = function() {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
}

function initPlatformPixel() {
  if (!window.fbq) return;
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.email) {
      const nameParts = (userData.name || '').trim().split(' ');
      const matchData = {};
      if (userData.email) matchData.em = userData.email;
      if (userData.phone) matchData.ph = userData.phone.replace(/[^0-9]/g, '');
      if (nameParts[0]) matchData.fn = nameParts[0].toLowerCase();
      if (nameParts.length > 1) matchData.ln = nameParts.slice(1).join(' ').toLowerCase();
      window.fbq('init', PLATFORM_PIXEL_ID, matchData);
    } else {
      window.fbq('init', PLATFORM_PIXEL_ID);
    }
  } catch (e) {
    window.fbq('init', PLATFORM_PIXEL_ID);
  }
}

// Fire a platform pixel event and navigate after a short delay to ensure the event is sent
function firePixelAndNavigate(eventName, eventData, url) {
  if (window.fbq) {
    window.fbq('trackSingle', PLATFORM_PIXEL_ID, eventName, eventData || {});
  }
  setTimeout(() => { window.location.href = url; }, 300);
}
window.firePixelAndNavigate = firePixelAndNavigate;

function PlatformPixelTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    const isMarketingPage = (
      path === '/' || path === '/precios' || path === '/login' || 
      path === '/register' || path === '/verify-email' || 
      path === '/forgot-password' || path === '/payment/success' || 
      path === '/auth/callback'
    );
    
    if (!isMarketingPage) return;
    
    // Prevent double PageView on same page
    if (_lastPixelPage === path) return;
    _lastPixelPage = path;
    
    loadFbPixelSDK();
    const timer = setTimeout(() => {
      initPlatformPixel();
      window.fbq('trackSingle', PLATFORM_PIXEL_ID, 'PageView');
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PlatformPixelTracker />
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
